import { Link } from "react-router-dom";
import RevealLine from "@/components/RevealLine";
import Reveal from "@/components/Reveal";
import { useLang } from "@/i18n";

const CHAPTERS = [
  {
    n: "01",
    title: { it: "Origine", en: "Origin" },
    year: "2016",
    body: {
      it: "Aéràs nasce dall'incontro tra ingegneria HVAC industriale e ricerca sui bioaerosol. La prima commessa è per un laboratorio farmaceutico che richiede aria di grado GMP-C.",
      en: "Aéràs is born at the intersection of industrial HVAC engineering and bioaerosol research. The first contract is for a pharmaceutical laboratory requiring GMP-C grade air.",
    },
  },
  {
    n: "02",
    title: { it: "Ricerca", en: "Research" },
    year: "2019",
    body: {
      it: "Apriamo il laboratorio interno di validazione filtri e sensori. Sviluppiamo la nostra libreria di algoritmi predittivi per la manutenzione degli impianti in cleanroom.",
      en: "We open our in-house filter and sensor validation lab. We develop our library of predictive algorithms for cleanroom system maintenance.",
    },
  },
  {
    n: "03",
    title: { it: "IoT", en: "IoT" },
    year: "2022",
    body: {
      it: "Debutto della gamma AIR FORCE ONE. Sensoristica intelligente e controllo via App diventano parte integrante di ogni purificatore: l'aria diventa un dato in tempo reale.",
      en: "Debut of the AIR FORCE ONE range. Smart sensors and App control become an integral part of every purifier: air becomes real-time data.",
    },
  },
  {
    n: "04",
    title: { it: "Alleanza", en: "Alliance" },
    year: "2025",
    body: {
      it: "Partnership strategica con Carbonplai. Ogni impianto Aéràs ora è tracciato, misurato e compensato in termini di CO₂ equivalente. L'aria pulita diventa carbon-neutral.",
      en: "Strategic partnership with Carbonplai. Every Aéràs system is now tracked, measured and offset in CO₂ equivalent. Clean air becomes carbon-neutral.",
    },
  },
];

export default function About() {
  const { lang, tr } = useLang();
  return (
    <div data-testid="page-about" className="bg-white text-[#0a0a0a] pt-32 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-8">
          {tr({ it: "— CHI SIAMO / SINCE 2016", en: "— ABOUT US / SINCE 2016" })}
        </div>
        <h1 className="font-display text-[clamp(3rem,10vw,10rem)] leading-[0.86] tracking-tighter max-w-6xl">
          {lang === "en" ? (
            <>
              <div><RevealLine delay={0.2}>An air</RevealLine></div>
              <div><RevealLine delay={0.35}>laboratory.</RevealLine></div>
              <div>
                <RevealLine delay={0.5} className="font-editorial italic font-normal text-[#3f3f46]">
                  Not a supplier.
                </RevealLine>
              </div>
            </>
          ) : (
            <>
              <div><RevealLine delay={0.2}>Un laboratorio</RevealLine></div>
              <div><RevealLine delay={0.35}>di aria.</RevealLine></div>
              <div>
                <RevealLine delay={0.5} className="font-editorial italic font-normal text-[#3f3f46]">
                  Non un fornitore.
                </RevealLine>
              </div>
            </>
          )}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-20">
          <Reveal className="lg:col-span-6 lg:col-start-7">
            <p className="font-body text-lg text-[#3f3f46] leading-relaxed">
              {tr({
                it: "Aéràs Technology progetta e installa sistemi di purificazione, monitoraggio e trattamento dell'aria per contesti B2B ad alta performance: industria farmaceutica, sanità, ricerca, uffici corporate, hospitality di lusso. Ogni progetto è custom, ogni impianto è tracciato, ogni scelta è documentata.",
                en: "Aéràs Technology designs and installs air purification, monitoring and treatment systems for high-performance B2B contexts: pharmaceutical industry, healthcare, research, corporate offices, luxury hospitality. Every project is custom, every system is tracked, every choice is documented.",
              })}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mt-32 border-t border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-24">
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-6">
            {tr({ it: "— CAPITOLI", en: "— CHAPTERS" })}
          </div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter mb-24">
            {tr({ it: "Quattro momenti che ci hanno definito.", en: "Four moments that defined us." })}
          </h2>

          {CHAPTERS.map((c) => (
            <Reveal key={c.n}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 border-t border-[#e4e4e7] py-12 md:py-20 group">
                <div className="md:col-span-1 font-mono text-[10px] tracking-[0.25em] text-[#3f3f46] pt-4">
                  {c.year}
                </div>
                <div className="md:col-span-4 relative">
                  <div className="font-editorial italic text-[clamp(4rem,10vw,9rem)] leading-[0.8] text-[#0a0a0a] group-hover:text-[#007A8A] transition-colors duration-700">
                    {c.n}
                  </div>
                </div>
                <div className="md:col-span-7 md:col-start-6">
                  <h3 className="font-display text-3xl md:text-5xl tracking-tighter mb-6">{tr(c.title)}</h3>
                  <p className="font-body text-lg text-[#3f3f46] leading-relaxed max-w-xl">{tr(c.body)}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-24 border-t border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <Reveal className="lg:col-span-6">
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-6">— TEAM</div>
              <h3 className="font-display text-4xl md:text-6xl tracking-tighter mb-8">
                {tr({ it: "Ingegneri.", en: "Engineers." })}<br />
                {tr({ it: "Ricercatori.", en: "Researchers." })}<br />
                <span className="font-editorial italic font-normal text-[#3f3f46]">
                  {tr({ it: "Artigiani dell'aria.", en: "Craftsmen of air." })}
                </span>
              </h3>
              <p className="font-body text-[#3f3f46] leading-relaxed max-w-md">
                {tr({
                  it: "Un team multidisciplinare — impiantistica, biologia, IoT, data science — che condivide un'unica ossessione: la qualità misurabile di ciò che respiriamo.",
                  en: "A multidisciplinary team — plant engineering, biology, IoT, data science — sharing a single obsession: the measurable quality of what we breathe.",
                })}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mt-24 border-t border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-24 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <h4 className="font-display text-4xl md:text-6xl tracking-tighter max-w-2xl">
            {tr({ it: "Vuoi lavorare con noi", en: "Want to work with us" })}<br />
            <span className="font-editorial italic font-normal">{tr({ it: "o per noi?", en: "or for us?" })}</span>
          </h4>
          <Link to="/contatti" className="btn-primary" data-testid="about-cta">
            {tr({ it: "Scrivici", en: "Write to us" })} →
          </Link>
        </div>
      </section>
    </div>
  );
}
